import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cssThemedValue, Typography as T } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { createMerchantAppLogin } from 'apis/restaurant-api/merchant-admin/venue/api';
import { Role } from 'apis/restaurant-api/merchant-admin/venue/types';
import { APIErrorNotification, RFFFieldErrorWrapper, WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';

import RoleSelect, { isRole } from './RoleSelect';
import VenueRolesInfo from './VenueRolesInfo';
import { stripMerchantAppLoginPrefix } from '../utils';

type Props = {
  isOpen: boolean;
  venueId: string;
  onClose: () => void;
  onDone: () => void;
};

const Root = styled(WoltModal)`
  width: 100%;
  max-width: 560px;
  background: ${cssThemedValue('surfaceMain')};
  border-radius: 8px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Actions = styled.div`
  display: flex;
`;

const Password = styled.div`
  width: 100%;
  margin-block-start: 16px;
  text-align: center;
  font-family: Menlo, Monaco, 'Courier New', monospace;
  border: 1px solid ${cssThemedValue('borderPrimary')};
  padding: 8px;
  border-radius: 4px;
`;

const StyledApiErrorNotification = styled(APIErrorNotification)`
  width: 100%;
  margin-block-end: 16px;
`;

type FormValues = {
  login: string;
  role: Role;
};

const initialFormValues: FormValues = {
  login: '',
  role: 'store_manager',
};

const PasswordCreateModal = ({ isOpen, venueId, onClose, onDone }: Props) => {
  const intl = useIntl();
  const [username, setUsername] = useState('');
  const { data, mutate, isLoading, error, reset } = useMutation(
    ({ login, role }: FormValues) => createMerchantAppLogin(venueId, { login, role }),
    {
      onSuccess: ({ login }) => {
        setUsername(login);
      },
    },
  );

  const handleClose = useCallback(() => {
    if (data) {
      onDone();
    }
    setUsername('');
    reset();
    onClose();
  }, [data, onClose, onDone, reset]);

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="userManagement.addCredentials" />
      </ModalHeader>
      {data && (
        <>
          <ModalContent>
            <T.Body3>
              <FormattedMessage
                id="userManagement.addedPasswordDescription"
                values={{
                  strong: (text: string) => <strong>{text}</strong>,
                  username: stripMerchantAppLoginPrefix(username),
                }}
              />
            </T.Body3>
            <Password>{data.password}</Password>
          </ModalContent>
          <StyledModalFooter>
            <Button variant="blue" size="small" onClick={handleClose}>
              <FormattedMessage id="state.done" />
            </Button>
          </StyledModalFooter>
        </>
      )}

      {!data && (
        <Form<FormValues>
          validate={validateYupSchema(
            yup.object({
              login: yup.string().required(),
              role: yup.mixed<Role>().required(),
            }),
          )}
          initialValues={initialFormValues}
          onSubmit={values => {
            mutate(values);
          }}
        >
          {({ handleSubmit, hasValidationErrors }) => (
            <>
              <ModalContent>
                <FormRow required label={intl.formatMessage({ id: 'userManagement.username' })}>
                  <Field name="login">
                    {field => (
                      <RFFFieldErrorWrapper field={field}>
                        {input => <Input {...input} disabled={isLoading} />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                </FormRow>
                <FormRow required label={intl.formatMessage({ id: 'userManagement.userRights' })}>
                  <Field name="role">
                    {field => (
                      <RoleSelect
                        {...field.input}
                        disabled={isLoading}
                        onChange={data => {
                          if (!isRole(data)) {
                            return;
                          }
                          field.input.onChange(data);
                        }}
                      />
                    )}
                  </Field>
                </FormRow>
                <VenueRolesInfo />
              </ModalContent>
              <StyledModalFooter>
                {error && <StyledApiErrorNotification error={error} />}
                {!data && (
                  <Actions>
                    <Button variant="lightBlue" size="small" onClick={handleClose} disabled={isLoading}>
                      <FormattedMessage id="state.cancel" />
                    </Button>
                    <Button
                      style={{ marginInlineStart: 16 }}
                      variant="blue"
                      size="small"
                      onClick={handleSubmit}
                      disabled={isLoading || hasValidationErrors}
                      loading={isLoading}
                    >
                      <FormattedMessage id="state.save" />
                    </Button>
                  </Actions>
                )}
              </StyledModalFooter>
            </>
          )}
        </Form>
      )}
    </Root>
  );
};

export default PasswordCreateModal;
